<script setup lang="ts">
    import { ref, computed, watch, onMounted, toRef } from 'vue'

    import { guid } from '@common/Guid'
    import type { AlternativeEditorAttributeFieldReadModel } from '@Alternatives/Alternatives/Domain/readmodels'
    import type { ValidationError } from '@common/types'

    // ---------------- props ----------------

    const props = defineProps<{
        propF: AlternativeEditorAttributeFieldReadModel
        propErrors: ValidationError[]
    }>()

    // ---------------- shortcuts ----------------

    const f = toRef(props, 'propF')
    const errors = toRef(props, 'propErrors')

    // ---------------- derived ----------------

    const error = computed(() =>
        errors.value?.find(e =>
            e.Context.startsWith(`AlternativeAttribute:${f.value.Id}`)
        )?.Error
    )

    // ---------------- local state ----------------

    const values = ref<ValueViewModel[]>([])

    // ---------------- watch ----------------

    watch(values, () => 
    {
        updateReadModel()
    },
    { deep: true })

    // ---------------- lifecycle ----------------

    onMounted(() =>
    {
        values.value = f.value.Values.map(v => new ValueViewModel(v))
    })

    // ---------------- methods ----------------

    function updateReadModel()
    {
        f.value.Values = values.value.map(v => v.Value)
    }

    function addValue()
    {
        values.value.push(new ValueViewModel(null))
    }

    function removeValue(v: ValueViewModel)
    {
        const index = values.value.indexOf(v)
        if (index >= 0)
        {
            values.value.splice(index, 1)
        }
    }

    // ---------------- local VM ----------------

    class ValueViewModel
    {
        Id: string = guid()
        Value: number | null = null
        Error: string | null = null

        constructor(value: number | null)
        {
            this.Value = value
        }
    }
</script>


<template>
    <div class="form-group">
        <label>
            <span v-if="f.IsRequired" style="color: red;">*</span> {{f.Name}}
            <span v-if="f.IsReadonly === false">(<a @click="addValue" class="text-primary">add</a>)</span>
        </label>
        <div v-for="v in values" :key="v.Id">
            <div class="input-group">

                <input type="number" class="form-control" placeholder="Number..." title="Please enter number"
                       v-model.number="v.Value"
                       :readonly="f.IsReadonly === true"
                       :min="f.MinValue != null ? f.MinValue : null"
                       :max="f.MaxValue != null ? f.MaxValue : null"
                       step="any" />

                <div class="input-group-append">
                    <button :disabled="f.IsReadonly === true" @click="removeValue(v)" class="btn btn-outline-secondary" type="button">
                        <span class="fa fa-times"></span>
                    </button>
                </div>
            </div>

            <div class="error">{{v.Error}}</div>
        </div>
        <div class="error">{{error}}</div>
        <small class="form-text text-muted" v-if="f.Description">{{f.Description}}</small>
    </div>
</template>